import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import Button from './UI/Button';

// The steps an application moves through before a decision is made
// (approved, rejected or cancelled). Which loan is in process, and whether it
// is the client's turn, is the API's call (my-summary, loan.state).
const WHAT_NEXT = {
  Pending: 'Capture your photo and sign the contract',
  'Awaiting Staff': 'Our staff are reviewing your application',
  'Awaiting Client': 'Staff sent it back — review and resubmit',
  'Awaiting Approval': 'Waiting for a final decision',
};

// Props:
// - loans: array of loans (GET /api/loans/my-loans)
// - summary: GET /api/loans/my-summary; in_process_loan is the application
//   to show (one sent back to the client comes first)
// - onContinue: fn(loan)
// - fallback: what to show instead when no application is in progress
const ApplicationStepper = ({ loans = [], summary = null, onContinue, fallback = null }) => {
  const inProcessId = summary?.in_process_loan?.id;
  const loan = inProcessId ? (loans || []).find(l => l.id === inProcessId) : null;

  if (!loan) return fallback;

  const sentBack = loan.status === 'Awaiting Client';
  // Awaiting Client only appears when staff sent the loan back.
  const steps = sentBack
    ? ['Pending', 'Awaiting Staff', 'Awaiting Client', 'Awaiting Approval', 'Decision']
    : ['Pending', 'Awaiting Staff', 'Awaiting Approval', 'Decision'];
  const current = steps.indexOf(loan.status);
  const clientTurn = loan.state?.actions?.sign === true || loan.state?.actions?.resubmit === true;

  return (
    <div className={`rounded-lg shadow p-4 min-w-0 ${sentBack
      ? 'bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 dark:border-amber-500/70'
      : 'bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-200">Application Status</h2>
        <span className="text-xs text-gray-500 dark:text-slate-400">{loan.loan_code || `Loan #${loan.id}`}</span>
      </div>
      <div className="space-y-3">
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
          {steps.map((label, i) => {
            const done = i < current;
            const active = i === current;
            return (
              <div
                key={label}
                title={label}
                className={`min-w-0 flex items-center justify-center text-center px-1 py-2 rounded-lg text-[11px] leading-tight font-semibold ${
                  done
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40'
                    : active
                    ? (sentBack ? 'bg-amber-600 text-white shadow-sm' : 'bg-blue-600 text-white shadow-sm')
                    : 'bg-gray-100 text-gray-500 border border-gray-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600'
                }`}
              >
                <span className="break-words">
                  {done && <CheckCircle2 size={12} className="inline mr-0.5 -mt-0.5" />}
                  {label}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-gray-600 dark:text-slate-400">{WHAT_NEXT[loan.status]}</div>
          {clientTurn && typeof onContinue === 'function' && (
            <Button variant="primary" onClick={() => onContinue(loan)}>{sentBack ? 'Review and resubmit' : 'Continue Application'}</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationStepper;
